import { ACCOUNT_APP_DISPLAY_NAME, ACCOUNT_APP_NAME, ACCOUNT_APP_SUBDOMAIN, CAD_APP_DISPLAY_NAME, CAD_APP_NAME, CAD_APP_SUBDOMAIN, CLINIC_APP_DISPLAY_NAME, CLINIC_APP_NAME, CLINIC_APP_SUBDOMAIN, LAB_APP_DISPLAY_NAME, LAB_APP_NAME, LAB_APP_SUBDOMAIN, LANDING_APP_DISPLAY_NAME, LANDING_APP_NAME, LANDING_APP_SUBDOMAIN, PORTAL_APP_DISPLAY_NAME, PORTAL_APP_NAME, PORTAL_APP_SUBDOMAIN } from '@dental-ui/shared/consts';

export interface AppConfig {
  name: string;
  subdomain: string;
  displayName: string;
  enabled: boolean;
  icon?: string;
  devPort?: number; // Development port for localhost
  useHash?: boolean; // Use HashLocationStrategy (/#/path)
}



export const APP_CONFIGS: Record<string, AppConfig> = {
  account: {
    name: ACCOUNT_APP_NAME,
    subdomain: ACCOUNT_APP_SUBDOMAIN,
    displayName: ACCOUNT_APP_DISPLAY_NAME,
    enabled: true,
    icon: 'home_work',
    devPort: 4202,
    useHash: true,
  },
  clinic: {
    name: CLINIC_APP_NAME,
    subdomain: CLINIC_APP_SUBDOMAIN,
    displayName: CLINIC_APP_DISPLAY_NAME,
    enabled: true,
    icon: 'local_hospital',
    devPort: 4203,
    useHash: true,
  },
  landing: {
    name: LANDING_APP_NAME,
    subdomain: LANDING_APP_SUBDOMAIN,
    displayName: LANDING_APP_DISPLAY_NAME,
    enabled: true,
    icon: 'home',
    devPort: 4200,
    useHash: true,
  },
  portal: {
    name: PORTAL_APP_NAME,
    subdomain: PORTAL_APP_SUBDOMAIN,
    displayName: PORTAL_APP_DISPLAY_NAME,
    enabled: true,
    icon: 'dashboard',
    devPort: 4201,
    useHash: true,
  },
  cad: {
    name: CAD_APP_NAME,
    subdomain: CAD_APP_SUBDOMAIN,
    displayName: CAD_APP_DISPLAY_NAME,
    enabled: true,
    icon: 'precision_manufacturing',
    devPort: 4205,
    useHash: true,
  },
  lab: {
    name: LAB_APP_NAME,
    subdomain: LAB_APP_SUBDOMAIN,
    displayName: LAB_APP_DISPLAY_NAME,
    enabled: true,
    icon: 'science',
    devPort: 4204,
    useHash: true,
  },
};

/**
 * Base domain'i getirir (örn: 'autobar.design' veya geliştirme için 'localhost')
 */
export function getBaseDomain(): string {
  const hostname = window.location.hostname;

  // For localhost development (ports 4200, 4201, etc.)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'localhost';
  }

  // Production: All apps run under .autobar.design domain
  if (hostname === 'autobar.design' || hostname.endsWith('.autobar.design')) {
    return 'autobar.design';
  }

  // Fallback: Extract base domain from subdomain
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    // Return last two parts (e.g., 'example.com')
    return parts.slice(-2).join('.');
  }

  return hostname;
}

/**
 * Mevcut hostname'den subdomain'i getirir
 */
export function getCurrentSubdomain(): string | null {
  const hostname = window.location.hostname;

  // For localhost, check port or path
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // In development, apps might run on different ports
    // This can be configured per environment
    return null; // Will use default subdomain from config
  }

  const parts = hostname.split('.');
  if (parts.length > 2) {
    return parts[0];
  }

  return null;
}

/**
 * Belirli bir uygulama subdomain'i için URL oluşturur
 */
export function buildAppUrl(appName: string, path = ''): string {
  const config = APP_CONFIGS[appName];
  if (!config || !config.enabled) {
    console.warn(`App ${appName} is not configured or not enabled`);
    return '#';
  }

  const baseDomain = getBaseDomain();
  const protocol = window.location.protocol;
  const currentHostname = window.location.hostname;
  const currentPort = window.location.port;

  // Hash handling logic
  let finalPath = path;
  if (config.useHash) {
    // Path varsa, başına / eklendiğinden ve /#/ ile başladığından emin ol
    // Çift slash oluşmasını (/#//path) engelle
    if (finalPath) {
      if (finalPath.startsWith('/')) {
        finalPath = finalPath.slice(1);
      }
      finalPath = '/#/' + finalPath;
    } else {
      // Path yoksa sadece root hash
      finalPath = '/#/';
    }
  }

  // For localhost development
  if (baseDomain === 'localhost' || baseDomain === '127.0.0.1') {
    // Use configured development port for each app
    const devPort = config.devPort || 4200;
    return `${protocol}//${baseDomain}:${devPort}${finalPath}`;
  }

  // Production and Dev environment: use subdomain
  // All apps use subdomain format: {subdomain}.autobar.design
  // If we are on dev environment (dev-*.autobar.design), target should also be dev-*.autobar.design
  let targetSubdomain = config.subdomain;
  
  if (currentHostname.startsWith('dev-') || currentHostname.includes('dev-')) {
    if (!targetSubdomain.startsWith('dev-')) {
      targetSubdomain = `dev-${targetSubdomain}`;
    }
  }

  const subdomain = `${targetSubdomain}.`;
  const port = currentPort && currentPort !== '80' && currentPort !== '443' ? `:${currentPort}` : '';

  return `${protocol}//${subdomain}${baseDomain}${port}${finalPath}`;
}

